import type {
  CommitRow,
  CrmField,
  ImportMode,
  OrganisationChoice,
  PreviewRow,
  RowAction,
  RowChoice,
  RowInput,
  RowOutcome,
  RowStatus,
} from './types'

export const STATUS_LABELS: Record<RowStatus, string> = {
  nieuw: 'Nieuw',
  nieuw_org: 'Nieuw, met nieuwe organisatie',
  bestaat: 'Bestaat al',
  bestaat_persoon: 'Bestaat als persoon',
  founder: 'Founder',
  mogelijk_dubbel: 'Mogelijk dubbel',
  dubbel_in_bestand: 'Dubbel in bestand',
  fout: 'Fout',
}

/** The status filters of ③ Controleren; a filter can cover several statuses. */
export type StatusFilter = 'alle' | 'nieuw' | 'bestaand' | 'mogelijk' | 'founder' | 'dubbel' | 'fout'

export const FILTER_LABELS: Record<StatusFilter, string> = {
  alle: 'Alle',
  nieuw: 'Nieuw',
  bestaand: 'Bestaand',
  mogelijk: 'Mogelijk dubbel',
  founder: 'Founder',
  dubbel: 'Dubbel in bestand',
  fout: 'Fout',
}

export const FILTER_ORDER: readonly Exclude<StatusFilter, 'alle'>[] = [
  'nieuw',
  'bestaand',
  'mogelijk',
  'founder',
  'dubbel',
  'fout',
]

const FILTER_OF: Record<RowStatus, Exclude<StatusFilter, 'alle'>> = {
  nieuw: 'nieuw',
  nieuw_org: 'nieuw',
  bestaat: 'bestaand',
  bestaat_persoon: 'bestaand',
  founder: 'founder',
  mogelijk_dubbel: 'mogelijk',
  dubbel_in_bestand: 'dubbel',
  fout: 'fout',
}

export function filterOf(status: RowStatus): Exclude<StatusFilter, 'alle'> {
  return FILTER_OF[status]
}

export function countByFilter(rows: readonly PreviewRow[]): Record<Exclude<StatusFilter, 'alle'>, number> {
  const counts = { nieuw: 0, bestaand: 0, mogelijk: 0, founder: 0, dubbel: 0, fout: 0 }
  for (const row of rows) counts[filterOf(row.status)] += 1
  return counts
}

export const OUTCOME_LABELS: Record<RowOutcome, string> = {
  created: 'Aangemaakt',
  filled: 'Aangevuld',
  overwritten: 'Overschreven',
  linked: 'Gekoppeld',
  skipped: 'Overgeslagen',
  failed: 'Mislukt',
}

export const OUTCOME_ORDER: readonly RowOutcome[] = [
  'created',
  'filled',
  'overwritten',
  'linked',
  'skipped',
  'failed',
]

/** The fields shown as editable cells in the preview, with their column width. */
export interface FieldColumn {
  field: Exclude<CrmField, 'first_name' | 'last_name'>
  label: string
  width: string
}

export const NAME_COLUMN: FieldColumn = { field: 'name', label: 'Naam', width: 'w-48' }

const OTHER_CONTACT_COLUMNS: readonly FieldColumn[] = [
  { field: 'email', label: 'E-mail', width: 'w-48' },
  { field: 'organisation', label: 'Organisatie', width: 'w-44' },
  { field: 'job_title', label: 'Functie', width: 'w-36' },
  { field: 'phone', label: 'Telefoon', width: 'w-32' },
  { field: 'website', label: 'Website organisatie', width: 'w-40' },
  { field: 'city', label: 'Plaats', width: 'w-32' },
  { field: 'category', label: 'Categorie', width: 'w-32' },
  { field: 'labels', label: 'Labels', width: 'w-36' },
  { field: 'owner', label: 'Eigenaar', width: 'w-32' },
]

const OTHER_ORGANISATION_COLUMNS: readonly FieldColumn[] = [
  { field: 'website', label: 'Website', width: 'w-44' },
  { field: 'kvk_number', label: 'KvK', width: 'w-24' },
  { field: 'phone', label: 'Telefoon', width: 'w-32' },
  { field: 'address', label: 'Adres', width: 'w-40' },
  { field: 'postcode', label: 'Postcode', width: 'w-24' },
  { field: 'city', label: 'Plaats', width: 'w-32' },
  { field: 'category', label: 'Categorie', width: 'w-32' },
  { field: 'labels', label: 'Labels', width: 'w-36' },
  { field: 'owner', label: 'Eigenaar', width: 'w-32' },
]

/**
 * The editable columns for a mode: the organisation first in organisations mode (it is the
 * record), then the other mapped fields. A column nobody mapped is left out, so the grid
 * stays as narrow as the file.
 */
export function columnsFor(mode: ImportMode, mapped: ReadonlySet<CrmField>): {
  lead: FieldColumn
  others: FieldColumn[]
} {
  const mappedAny = (field: CrmField) =>
    mapped.has(field) || (field === 'name' && (mapped.has('first_name') || mapped.has('last_name')))
  if (mode === 'organisaties') {
    return {
      lead: { field: 'organisation', label: 'Organisatie', width: 'w-56' },
      others: OTHER_ORGANISATION_COLUMNS.filter((column) => mappedAny(column.field)),
    }
  }
  return { lead: NAME_COLUMN, others: OTHER_CONTACT_COLUMNS.filter((column) => mappedAny(column.field)) }
}

/** The fields a mapping feeds (a column mapped to nothing is left out). */
export function mappedFields(mapping: readonly (CrmField | null)[]): Set<CrmField> {
  return new Set(mapping.filter((field): field is CrmField => field !== null))
}

export function defaultChoice(row: PreviewRow): RowChoice {
  return {
    action: row.default_action,
    targetOrganisationId: row.organisation.similar[0]?.id ?? null,
    organisationChoice: null,
    organisationId: null,
  }
}

/** Whether the chosen action and organisation answer are still possible after a re-check. */
export function isOffered(row: PreviewRow, choice: RowChoice): boolean {
  if (choice.action !== null && !row.actions.includes(choice.action)) return false
  if (choice.organisationChoice === 'match') {
    return row.organisation.similar.some((item) => item.id === choice.organisationId)
  }
  if (choice.organisationChoice !== null && !row.organisation_choice_required) return false
  return true
}

/** A row that takes the file's data and needs no look-alike organisation settled. */
function importsRow(choice: RowChoice): boolean {
  return choice.action !== null && choice.action !== 'skip' && choice.action !== 'merge'
}

/** Whether the row still has to settle which organisation it names. */
export function needsOrganisationChoice(row: PreviewRow, choice: RowChoice): boolean {
  return row.organisation_choice_required && importsRow(choice) && choice.organisationChoice === null
}

/** How many rows wait for an answer: an action, or which look-alike organisation is meant. */
export function undecided(rows: readonly PreviewRow[], choices: readonly RowChoice[]): number {
  return rows.filter((row, index) => {
    const choice = choices[index]
    return choice.action === null || needsOrganisationChoice(row, choice)
  }).length
}

/** What the import will do: rows made new, and rows added to what exists. */
export function plannedCounts(
  rows: readonly PreviewRow[],
  choices: readonly RowChoice[],
): { created: number; known: number } {
  let created = 0
  let known = 0
  rows.forEach((_row, index) => {
    const action = choices[index]?.action
    if (action === 'new' || action === 'add_profile') created += 1
    else if (action === 'fill' || action === 'overwrite' || action === 'link') known += 1
  })
  return { created, known }
}

export function actionLabel(action: RowAction, row: PreviewRow, mode: ImportMode): string {
  switch (action) {
    case 'new':
      return mode === 'organisaties' ? 'Nieuwe organisatie' : 'Nieuw contact'
    case 'fill':
      return 'Aanvullen (lege velden)'
    case 'overwrite':
      return 'Overschrijven'
    case 'add_profile':
      return row.status === 'founder' ? 'Toch als relatie toevoegen' : 'Als relatie toevoegen'
    case 'link':
      if (mode === 'organisaties') return 'Dezelfde organisatie'
      return row.contact ? `Dezelfde als ${row.contact.full_name}` : 'Dezelfde persoon'
    case 'merge':
      return row.duplicate_of_row === null ? 'Samenvoegen' : `Samenvoegen met rij ${row.duplicate_of_row}`
    case 'skip':
      return 'Overslaan'
  }
}

export const ORGANISATION_NEW = 'new'
export const ORGANISATION_NONE = 'none'

/** The select value for an organisation answer: "match:<id>", "new", "none" or "" (not chosen). */
export function organisationValue(choice: RowChoice): string {
  if (choice.organisationChoice === 'match') return `match:${choice.organisationId ?? ''}`
  return choice.organisationChoice ?? ''
}

export function parseOrganisationValue(value: string): {
  organisationChoice: OrganisationChoice | null
  organisationId: string | null
} {
  if (value.startsWith('match:')) return { organisationChoice: 'match', organisationId: value.slice(6) }
  if (value === ORGANISATION_NEW) return { organisationChoice: 'new', organisationId: null }
  if (value === ORGANISATION_NONE) return { organisationChoice: 'none', organisationId: null }
  return { organisationChoice: null, organisationId: null }
}

function score(value: number | null): string {
  return value === null ? '' : `, naam ${value.toFixed(2).replace('.', ',')}`
}

const ORGANISATION_REASONS = {
  kvk: 'zelfde KvK-nummer',
  website: 'zelfde website',
  naam: 'zelfde naam',
  domein: 'zelfde e-maildomein',
} as const

/** Why a row matches a contact, in a line under the row. */
export function contactLine(row: PreviewRow): string | null {
  const contact = row.contact
  if (!contact) return null
  if (contact.reason === 'email') return `Zelfde e-mailadres als ${contact.full_name}`
  if (contact.reason === 'naam') return `Zelfde naam als ${contact.full_name}${contact.email ? ` (${contact.email})` : ''}`
  return `Lijkt op ${contact.full_name}${contact.email ? ` (${contact.email})` : ''}${score(contact.score)}`
}

/** What the row's organisation is, in lines under the row. */
export function organisationLines(row: PreviewRow): string[] {
  const { organisation } = row
  if (organisation.state === 'bestaat' && organisation.match) {
    const reason = organisation.reason ? ` (${ORGANISATION_REASONS[organisation.reason]})` : ''
    return [`Organisatie bestaat al: ${organisation.match.name}${reason}`]
  }
  return organisation.similar.map(
    (item) => `Organisatie lijkt op ${item.name}${item.city ? ` (${item.city})` : ''}${score(item.score)}`,
  )
}

/** The rows as the commit takes them: the file's fields plus what the user chose. */
export function commitRows(
  mode: ImportMode,
  inputs: readonly RowInput[],
  rows: readonly PreviewRow[],
  choices: readonly RowChoice[],
): CommitRow[] {
  return inputs.map((input, index) => {
    const row = rows[index]
    const choice = choices[index]
    const action = choice.action ?? 'skip'
    const choosing = row.organisation_choice_required && importsRow(choice)
    return {
      ...input,
      action,
      person_id: mode === 'contacten' && action === 'link' ? (row.contact?.person_id ?? null) : null,
      target_organisation_id:
        mode === 'organisaties' && action === 'link'
          ? (choice.targetOrganisationId ?? row.organisation.similar[0]?.id ?? null)
          : null,
      organisation_choice: choosing ? choice.organisationChoice : null,
      organisation_id: choosing && choice.organisationChoice === 'match' ? choice.organisationId : null,
    }
  })
}
